import { gql } from '@apollo/client';

/**
 * A brokerage's branding and its case and task configuration. All of it is
 * `organisation_config` on that brokerage; reads hang off
 * `brokerage(id:)` so the tenant comes from the object, never the request.
 */

export interface Branding {
  id: string;
  companyName: string | null;
  website: string | null;
  logoFileId: string | null;
  logo: { id: string; filename: string | null; previewUrl: string | null } | null;
}

export const BRANDING_FIELDS = `
  id
  companyName
  website
  logoFileId
  logo {
    id
    filename
    previewUrl: originalDownloadUrl(disposition: INLINE)
  }
`;

export const BRANDING_QUERY = gql`
  query BrokerageBranding($tenantId: UUID!) {
    brokerage(id: $tenantId) {
      ${BRANDING_FIELDS}
    }
  }
`;

export interface BrandingResult {
  brokerage: Branding;
}

export const SET_TENANT_BRANDING = gql`
  mutation SetTenantBranding($input: TenantBrandingInput!) {
    setTenantBranding(input: $input) {
      ${BRANDING_FIELDS}
    }
  }
`;

export type DimensionKind = 'caseStatus' | 'caseSource' | 'taskType' | 'taskStatus';

export interface Dimension {
  id: string;
  key: string | null;
  label: string | null;
  description: string | null;
  color?: string | null;
  position: number | null;
  isTerminalSuccess?: boolean | null;
  isTerminalFailure?: boolean | null;
}

const STATUS_FIELDS = 'id key label description color position isTerminalSuccess isTerminalFailure';
const PLAIN_FIELDS = 'id key label description position';

/** Every configuration's id, oldest first: the main app uses the first of each. */
export const CONFIGURATION_IDS_QUERY = gql`
  query BrokerageConfigurationIds($tenantId: UUID!) {
    brokerage(id: $tenantId) {
      id
      caseConfigurations {
        id
      }
      taskConfigurations {
        id
      }
    }
  }
`;

export interface ConfigurationIdsResult {
  brokerage: {
    id: string;
    caseConfigurations: { id: string }[];
    taskConfigurations: { id: string }[];
  };
}

/** The four lists of one case configuration and one task configuration. */
export const CONFIGURATION_QUERY = gql`
  query BrokerageConfiguration(
    $caseConfigurationId: UUID!
    $taskConfigurationId: UUID!
    $hasCase: Boolean!
    $hasTask: Boolean!
  ) {
    caseStatuses(caseConfigurationId: $caseConfigurationId) @include(if: $hasCase) { ${STATUS_FIELDS} }
    caseSources(caseConfigurationId: $caseConfigurationId) @include(if: $hasCase) { ${PLAIN_FIELDS} }
    taskTypes(taskConfigurationId: $taskConfigurationId) @include(if: $hasTask) { ${PLAIN_FIELDS} }
    taskStatuses(taskConfigurationId: $taskConfigurationId) @include(if: $hasTask) { ${STATUS_FIELDS} }
  }
`;

export interface ConfigurationResult {
  caseStatuses?: Dimension[];
  caseSources?: Dimension[];
  taskTypes?: Dimension[];
  taskStatuses?: Dimension[];
}

export const CREATE_CASE_CONFIGURATION = gql`
  mutation CreateCaseConfiguration($tenantId: UUID!) {
    createCaseConfiguration(input: {}, tenantId: $tenantId) {
      id
    }
  }
`;

export const CREATE_TASK_CONFIGURATION = gql`
  mutation CreateTaskConfiguration($tenantId: UUID!) {
    createTaskConfiguration(input: {}, tenantId: $tenantId) {
      id
    }
  }
`;

/** Per kind: which configuration it hangs off, and whether it carries a colour and an outcome. */
export const DIMENSIONS: Record<
  DimensionKind,
  {
    title: string;
    singular: string;
    parent: 'case' | 'task';
    parentField: 'caseConfigurationId' | 'taskConfigurationId';
    hasOutcome: boolean;
    create: ReturnType<typeof gql>;
    update: ReturnType<typeof gql>;
    remove: ReturnType<typeof gql>;
    reorder: ReturnType<typeof gql>;
  }
> = {
  caseStatus: {
    title: 'Case stages',
    singular: 'stage',
    parent: 'case',
    parentField: 'caseConfigurationId',
    hasOutcome: true,
    create: gql`
      mutation CreateCaseStatus($input: CaseStatusCreateInput!) {
        createCaseStatus(input: $input) { ${STATUS_FIELDS} }
      }
    `,
    update: gql`
      mutation UpdateCaseStatus($input: CaseStatusUpdateInput!) {
        updateCaseStatus(input: $input) { ${STATUS_FIELDS} }
      }
    `,
    remove: gql`
      mutation DeleteCaseStatus($id: UUID!, $fallbackId: UUID) {
        deleteCaseStatus(id: $id, fallbackId: $fallbackId)
      }
    `,
    reorder: gql`
      mutation ReorderCaseStatuses($input: ReorderInput!) {
        reorderCaseStatuses(input: $input) {
          id
          position
        }
      }
    `,
  },
  caseSource: {
    title: 'Case sources',
    singular: 'source',
    parent: 'case',
    parentField: 'caseConfigurationId',
    hasOutcome: false,
    create: gql`
      mutation CreateCaseSource($input: CaseSourceCreateInput!) {
        createCaseSource(input: $input) { ${PLAIN_FIELDS} }
      }
    `,
    update: gql`
      mutation UpdateCaseSource($input: CaseSourceUpdateInput!) {
        updateCaseSource(input: $input) { ${PLAIN_FIELDS} }
      }
    `,
    remove: gql`
      mutation DeleteCaseSource($id: UUID!, $fallbackId: UUID) {
        deleteCaseSource(id: $id, fallbackId: $fallbackId)
      }
    `,
    reorder: gql`
      mutation ReorderCaseSources($input: ReorderInput!) {
        reorderCaseSources(input: $input) {
          id
          position
        }
      }
    `,
  },
  taskType: {
    title: 'Task types',
    singular: 'task type',
    parent: 'task',
    parentField: 'taskConfigurationId',
    hasOutcome: false,
    create: gql`
      mutation CreateTaskType($input: TaskTypeCreateInput!) {
        createTaskType(input: $input) { ${PLAIN_FIELDS} }
      }
    `,
    update: gql`
      mutation UpdateTaskType($input: TaskTypeUpdateInput!) {
        updateTaskType(input: $input) { ${PLAIN_FIELDS} }
      }
    `,
    remove: gql`
      mutation DeleteTaskType($id: UUID!, $fallbackId: UUID) {
        deleteTaskType(id: $id, fallbackId: $fallbackId)
      }
    `,
    reorder: gql`
      mutation ReorderTaskTypes($input: ReorderInput!) {
        reorderTaskTypes(input: $input) {
          id
          position
        }
      }
    `,
  },
  taskStatus: {
    title: 'Task statuses',
    singular: 'task status',
    parent: 'task',
    parentField: 'taskConfigurationId',
    hasOutcome: true,
    create: gql`
      mutation CreateTaskStatus($input: TaskStatusCreateInput!) {
        createTaskStatus(input: $input) { ${STATUS_FIELDS} }
      }
    `,
    update: gql`
      mutation UpdateTaskStatus($input: TaskStatusUpdateInput!) {
        updateTaskStatus(input: $input) { ${STATUS_FIELDS} }
      }
    `,
    remove: gql`
      mutation DeleteTaskStatus($id: UUID!, $fallbackId: UUID) {
        deleteTaskStatus(id: $id, fallbackId: $fallbackId)
      }
    `,
    reorder: gql`
      mutation ReorderTaskStatuses($input: ReorderInput!) {
        reorderTaskStatuses(input: $input) {
          id
          position
        }
      }
    `,
  },
};
